import React from 'react';
import { formatNumber } from './format';

// Horizontal bar list. Used for top-tools and top-users-by-spend.
//
// Props:
//   data       — [{name, count, ...optional fields}]
//   onClick    — (row) => void   — optional, makes rows clickable (and focusable)
//   colorFn    — (row, i) => CSS background   — defaults to the brand accent.
//                Pass `synthetic: true` on a row to render it muted (used for the
//                "Deleted / aged-out" residual).
//   labelKey   — defaults to 'name'
//   valueKey   — defaults to 'count'
//   nameWidth  — px width of the left label column (default 160)
//   formatVal  : (value, row) => string for the right-hand value (default formatNumber)
//   valueWidth : optional px width of the value column. Give it when the value text
//                varies in length (a figure plus a count): the column otherwise sizes to
//                each row's text and the bars stop lining up.
//   emptyText  — shown when data is empty (default 'No data yet')
export default function HBarChart({
  data,
  onClick,
  colorFn,
  labelKey = 'name',
  valueKey = 'count',
  nameWidth = 160,
  formatVal = formatNumber,
  valueWidth,
  emptyText = 'No data yet',
}) {
  if (!data || data.length === 0) {
    return <div className="tile-empty">{emptyText}</div>;
  }
  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);

  const defaultColor = (row) => row.synthetic
    ? 'var(--ink-4)'
    : 'linear-gradient(90deg, var(--series-1), var(--series-4))';

  return (
    <div className="hbar">
      {data.map((d, i) => {
        const Row = onClick ? 'button' : 'div';
        return (
          <Row
            key={d[labelKey] + ':' + i}
            type={onClick ? 'button' : undefined}
            className={`hbar-row${onClick ? ' is-clickable' : ''}${d.synthetic ? ' is-synthetic' : ''}`}
            onClick={onClick ? () => onClick(d) : undefined}
          >
            <code className="hbar-name" style={{ width: nameWidth }} title={String(d[labelKey])}>{d[labelKey]}</code>
            <span className="hbar-track">
              <span
                className="hbar-fill"
                style={{
                  width: `${((d[valueKey] || 0) / maxVal) * 100}%`,
                  background: colorFn ? colorFn(d, i) : defaultColor(d),
                }}
              />
            </span>
            <span className="hbar-value"
                  style={valueWidth ? { width: valueWidth, flexShrink: 0 } : undefined}>
              {formatVal(d[valueKey] || 0, d)}
            </span>
          </Row>
        );
      })}
    </div>
  );
}
